import type { ParticularKey } from './ParticularKey';

/**
 * Create a type guard that detects a given value under given key
 *
 * Note: we need a higher-order function to substitute partial inference:
 * TBase and TChild should be specified to define what the type guard does,
 * but TKey can be inferred from "key" parameter to avoid repeating
 * like `<Pet, Cat, 'type'>('type', 'cat')`.
 *
 * This function has one advantage over writing the type guard manually:
 * typescript checks that the type guard is "sane", I mean whether the value
 * you declare to be checked here really corresponds to the type declaration.
 *
 * Example:
 * ```
 * type Cat = {
 *   type: 'cat';
 *   cry: 'meow';
 * };
 *
 * type Dog = {
 *   type: 'dog';
 *   cry: 'woof';
 * }
 *
 * type Pet = Cat | Dog;
 *
 * // correct
 * // note the extra () - they are inevitable if we want to avoid repating 'type'
 * // as described above.
 * const isCat = makeTypeGuardByPropertyValue<Pet, Cat>()('type', 'cat');
 *
 * // wrong
 * // typescript will report error on any call that declares something
 * // not matching Cat's key->value pair, including common copy-paste errors
 * // when declaring multiple type guards for common base type.
 * const isCat = makeTypeGuardByPropertyValue<Pet, Cat>()('type', 'dog');
 * ```
 */
export const makeTypeGuardByPropertyValue =
  <TBase, TChild extends TBase>() =>
  <TKey extends keyof TChild>(key: ParticularKey<TKey>, value: TChild[TKey]) =>
  (obj: TBase | null | undefined): obj is TChild =>
    (obj as TChild)?.[key] === value;
